import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  TablePagination,
  Avatar,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Menu,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Close,
} from '@mui/icons-material';
import { User, UserFilterParams, TableColumn } from '../types';
import apiService from '../services/api';
import UserViewDialog from '../components/modals/UserViewDialog';
import UserRoleDialog from '../components/modals/UserRoleDialog';
import PermissionGuard from '../components/PermissionGuard';
import { Permissions, hasPermission } from '../utils/permissions';
import { useAuth } from '../context/useAuth';
import DataTable from '../components/dataTable';
import VisitorsEditDialog from '../components/modals/VisitorsEditDialog';
import { viewer } from '../types/user.type';

const Visitors: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [visitors, setVisitors] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  console.log('visitors', visitors);
  // Filters
  const [filters, setFilters] = useState<UserFilterParams>({
    page: 1,
    limit: 25,
    // searchTerm: '',
    isActive: undefined,
    role: 'VISITOR', // Fixed to Visitor
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [roleUser, setRoleUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<viewer | null>(null);

  const fetchVisitors = async () => {
    setLoading(true);
    try {
      const response = await apiService.getUsers(filters);
      setVisitors(response.data || []);
      setTotal(response.total || 0);
      // console.log("All visitor", response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch visitors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitors();
  }, [filters]);

  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setViewDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchVisitors();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete visitor');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await apiService.toggleUserStatus(user.id);
      fetchVisitors();
      setActionMenuAnchor(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to toggle visitor status'
      );
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.exportUsers(filters);
      const data = response.data || [];

      // Convert to CSV
      const csvContent = [
        Object.keys(data[0] || {}).join(','),
        ...data.map(item => Object.values(item).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visitors_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export visitors');
    }
  };

  const handleUserSaved = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    fetchVisitors();
  };

  const handleFilterChange = (field: keyof UserFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filtering
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const openActionMenu = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleRoleUpdated = () => {
    fetchVisitors();
    setRoleDialogOpen(false);
    setRoleUser(null);
  };

  const columns: TableColumn<User>[] = [
    {
      label: 'Name',
      keys: ['firstName', 'lastName', 'userType.typeName', 'profileImageUrl'],
      renderGroup: values => {
        const [firstName, lastName, type, profileImageUrl] = values as [
          string,
          string,
          string,
          string,
        ];
        const getInitials = (firstName: string, lastName: string) => {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        };
        return (
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          >
            <Avatar
              src={profileImageUrl}
              sx={{
                width: '40px',
                height: '40px',
                fontSize: '0.875rem',
                fontWeight: 500,
                backgroundColor: '#3730a3',
              }}
            >
              {getInitials(firstName, lastName)}
            </Avatar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: '0.75rem',
              }}
            >
              {firstName} {lastName}
              {/* <Chip
                label={type}
                size="small"
                sx={{
                  bgcolor: '#e0e7ff',
                  color: '#3730a3',
                  fontSize: '0.55rem',
                  fontWeight: 500,
                  height: 20,
                  marginTop: '3px',
                }}
              /> */}
            </Box>
          </Box>
        );
      },
    },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    // {
    //   label: 'Role',
    //   key: 'role.name',
    //   renderGroup: values => {
    //     const [role] = values as [string];
    //     return role ? (
    //       <Chip
    //         label={role}
    //         size="small"
    //         sx={{
    //           bgcolor: '#f0f9ff',
    //           color: '#0369a1',
    //           fontSize: '0.75rem',
    //           fontWeight: 500,
    //           height: 24,
    //         }}
    //       />
    //     ) : (
    //       <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
    //         No role
    //       </Typography>
    //     );
    //   },
    // },
    { label: 'Status', key: 'isActive', chip: 'status' },
    { label: 'Registered', key: 'createdAt' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#111827',
          }}
        >
          Visitors Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
            >
              <TextField
                placeholder="Search visitors..."
                value={filters.searchTerm}
                onChange={e => handleFilterChange('searchTerm', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PermissionGuard permission={Permissions.USER_EXPORT}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  sx={{ textTransform: 'none' }}
                >
                  Export
                </Button>
              </PermissionGuard>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<User>
          columns={columns}
          data={visitors}
          actions={row => [
            {
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleViewUser(row),
            },
            // {
            //   icon: <EditIcon fontSize="small" />,
            //   onClick: () => handleEditUser(row),
            // },
            {
              icon: <MoreVertIcon fontSize="small" />,
              onClick: e => openActionMenu(e, row),
            },
          ]}
        />

        {/* Pagination */}
        {visitors && visitors.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            border: '1px solid #e5e7eb',
            boxShadow:
              '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            mt: 1,
            minWidth: 150,
          },
        }}
      >
        {hasPermission(currentUser, Permissions.USER_UPDATE) && (
          <MenuItem
            onClick={() => selectedUser && handleToggleStatus(selectedUser)}
          >
            <ListItemIcon>
              {selectedUser?.isActive ? (
                <ToggleOffIcon fontSize="small" />
              ) : (
                <ToggleOnIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {selectedUser?.isActive ? 'Deactivate' : 'Activate'}
            </ListItemText>
          </MenuItem>
        )}
        {hasPermission(currentUser, Permissions.USER_DELETE) && (
          <MenuItem
            onClick={() => selectedUser && handleDeleteUser(selectedUser)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* View Dialog */}
      <UserViewDialog
        open={viewDialogOpen}
        user={viewingUser}
        onClose={() => {
          setViewDialogOpen(false);
          setViewingUser(null);
        }}
      />

      {/* Edit Dialog */}
      {editDialogOpen && (
        // <UserEditDialog
        //   open={editDialogOpen}
        //   user={editingUser}
        //   onClose={() => {
        //     setEditDialogOpen(false);
        //     setEditingUser(null);
        //   }}
        //   onSave={handleUserSaved}
        // />

        <VisitorsEditDialog
          open={editDialogOpen}
          user={editingUser}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          onSave={handleUserSaved}
        />
      )}

      {/* Role Management Dialog */}
      <UserRoleDialog
        open={roleDialogOpen}
        user={roleUser}
        onClose={() => {
          setRoleDialogOpen(false);
          setRoleUser(null);
        }}
        onSuccess={handleRoleUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete visitor "{userToDelete?.firstName}{' '}
            {userToDelete?.lastName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Visitors;
